import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import {
  getSubstitutionRules,
  saveSubstitutionRules,
  getTeacherTypesDirect,
} from '@timetable/api';
import type { SubstitutionRules, TeacherType } from '@timetable/types';
import { LoadingSpinner } from '../LoadingSpinner';

export const SubstitutionRulesTab: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const schoolContext = useAuthStore((state) => state.schoolContext);
  const queryClient = useQueryClient();

  // Determine effective school ID
  const effectiveSchoolId =
    user?.role === 'super_admin' ? schoolContext : user?.school_id;

  // Form state
  const [minFreePeriods, setMinFreePeriods] = useState(2);
  const [maxSubsPerDay, setMaxSubsPerDay] = useState(2);
  const [maxSubsPerWeek, setMaxSubsPerWeek] = useState(5);
  const [minFreePeriodsForSecondSub, setMinFreePeriodsForSecondSub] = useState(5);
  const [totalPeriodsPerDay, setTotalPeriodsPerDay] = useState(8);
  const [lunchStart, setLunchStart] = useState('12:00');
  const [lunchEnd, setLunchEnd] = useState('13:30');
  const [preferSubjectMatch, setPreferSubjectMatch] = useState(true);
  const [preferClassAssociation, setPreferClassAssociation] = useState(true);
  const [avoidConsecutive, setAvoidConsecutive] = useState(true);
  const [preferClassTeacherForSupervision, setPreferClassTeacherForSupervision] =
    useState(true);
  const [eligibleTeacherTypeIds, setEligibleTeacherTypeIds] = useState<
    string[]
  >([]);
  const [assignmentMode, setAssignmentMode] = useState<'auto' | 'suggest'>(
    'auto'
  );
  const [notificationChannel, setNotificationChannel] = useState<
    'email' | 'whatsapp' | 'both'
  >('email');
  const [autoGenerateEnabled, setAutoGenerateEnabled] = useState(true);
  const [autoGenerateTime, setAutoGenerateTime] = useState('17:00');
  const [lockTimeoutMinutes, setLockTimeoutMinutes] = useState(15);
  const [notificationSenderName, setNotificationSenderName] = useState('');

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  // Query substitution rules
  const { data: rules, isLoading: rulesLoading } = useQuery({
    queryKey: ['substitution-rules', effectiveSchoolId],
    queryFn: () => getSubstitutionRules(effectiveSchoolId!),
    enabled: !!effectiveSchoolId,
  });

  // Query teacher types
  const { data: teacherTypes, isLoading: teacherTypesLoading } = useQuery({
    queryKey: ['teacher-types'],
    queryFn: () => getTeacherTypesDirect(),
    enabled: !!effectiveSchoolId,
  });

  // Update form state when rules are loaded
  useEffect(() => {
    if (rules) {
      setMinFreePeriods(rules.min_free_periods);
      setMaxSubsPerDay(rules.max_subs_per_day);
      setMaxSubsPerWeek(rules.max_subs_per_week);
      setMinFreePeriodsForSecondSub(rules.min_free_periods_for_second_sub);
      setTotalPeriodsPerDay(rules.total_periods_per_day);
      setLunchStart(rules.lunch_start);
      setLunchEnd(rules.lunch_end);
      setPreferSubjectMatch(rules.prefer_subject_match);
      setPreferClassAssociation(rules.prefer_class_association);
      setAvoidConsecutive(rules.avoid_consecutive);
      setPreferClassTeacherForSupervision(rules.prefer_class_teacher_for_supervision);
      setEligibleTeacherTypeIds(rules.eligible_teacher_type_ids || []);
      setAssignmentMode(rules.assignment_mode);
      setNotificationChannel(rules.notification_channel);
      setAutoGenerateEnabled(rules.auto_generate_enabled);
      setAutoGenerateTime(rules.auto_generate_time);
      setLockTimeoutMinutes(rules.lock_timeout_minutes);
      setNotificationSenderName(rules.notification_sender_name || '');
    }
  }, [rules]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (updatedRules: SubstitutionRules) =>
      saveSubstitutionRules(effectiveSchoolId!, updatedRules),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['substitution-rules', effectiveSchoolId],
      });
      setValidationErrors({});
    },
    onError: (error: any) => {
      if (error.fields) {
        setValidationErrors(error.fields);
      }
    },
  });

  // Validate form
  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    // Validation 1: min_free_periods < total_periods_per_day (Req 10.5)
    if (minFreePeriods >= totalPeriodsPerDay) {
      errors.minFreePeriods =
        'Minimum free periods must be less than total periods per day';
    }

    // Validation 2: max_subs_per_day <= total_periods_per_day (Req 10.6)
    if (maxSubsPerDay > totalPeriodsPerDay) {
      errors.maxSubsPerDay =
        'Maximum substitutions per day must be less than or equal to total periods per day';
    }

    // Validation 3: lunch_start < lunch_end (Req 10.7)
    if (lunchStart >= lunchEnd) {
      errors.lunchStart = 'Lunch start time must be before lunch end time';
    }

    // Validation 4: auto_generate_time required when auto_generate_enabled (Req 10.9)
    if (autoGenerateEnabled && !autoGenerateTime) {
      errors.autoGenerateTime =
        'Auto-generate time is required when auto-generate is enabled';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const updatedRules: SubstitutionRules = {
      school_id: effectiveSchoolId!,
      min_free_periods: minFreePeriods,
      max_subs_per_day: maxSubsPerDay,
      max_subs_per_week: maxSubsPerWeek,
      min_free_periods_for_second_sub: minFreePeriodsForSecondSub,
      total_periods_per_day: totalPeriodsPerDay,
      lunch_start: lunchStart,
      lunch_end: lunchEnd,
      prefer_subject_match: preferSubjectMatch,
      prefer_class_association: preferClassAssociation,
      avoid_consecutive: avoidConsecutive,
      prefer_class_teacher_for_supervision: preferClassTeacherForSupervision,
      eligible_teacher_type_ids:
        eligibleTeacherTypeIds.length > 0 ? eligibleTeacherTypeIds : null,
      assignment_mode: assignmentMode,
      notification_channel: notificationChannel,
      auto_generate_enabled: autoGenerateEnabled,
      auto_generate_time: autoGenerateTime,
      lock_timeout_minutes: lockTimeoutMinutes,
      notification_sender_name: notificationSenderName || null,
      notification_sender_email: null,
    };

    saveMutation.mutate(updatedRules);
  };

  // Check if sender email is missing when notification channel includes email (Req 10.13)
  // Removed — emails now always go via the system Gmail account (GMAIL_USER)

  if (rulesLoading || teacherTypesLoading) {
    return <LoadingSpinner />;
  }

  // Show message if Super Admin hasn't selected a school
  if (user?.role === 'super_admin' && !schoolContext) {
    return (
      <Alert severity="info">
        Please select a school from the school context switcher to configure
        substitution rules.
      </Alert>
    );
  }

  // Show message if no school context available
  if (!effectiveSchoolId) {
    return (
      <Alert severity="warning">
        No school context available. Please contact your administrator.
      </Alert>
    );
  }

  // Admin-only check (Req 19.4)
  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return (
      <Alert severity="warning">
        Substitution rules configuration is only available to administrators.
      </Alert>
    );
  }

  return (
    <Stack spacing={4}>
      {/* Quota Settings */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quota Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Configure limits to prevent teacher overload.
          </Typography>

          <Grid container spacing={3} mt={1}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Minimum Free Periods"
                type="number"
                value={minFreePeriods}
                onChange={(e) => setMinFreePeriods(Number(e.target.value))}
                fullWidth
                required
                inputProps={{ min: 0 }}
                error={!!validationErrors.minFreePeriods}
                helperText={
                  validationErrors.minFreePeriods ||
                  'Minimum free periods a teacher must keep after being assigned (default: 2)'
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Maximum Substitutions Per Day"
                type="number"
                value={maxSubsPerDay}
                onChange={(e) => setMaxSubsPerDay(Number(e.target.value))}
                fullWidth
                required
                inputProps={{ min: 1 }}
                error={!!validationErrors.maxSubsPerDay}
                helperText={
                  validationErrors.maxSubsPerDay ||
                  'Maximum substitution periods per teacher per day (default: 2)'
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Maximum Substitutions Per Week"
                type="number"
                value={maxSubsPerWeek}
                onChange={(e) => setMaxSubsPerWeek(Number(e.target.value))}
                fullWidth
                required
                inputProps={{ min: 1 }}
                helperText="Maximum substitution periods per teacher per week (default: 5)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Minimum Free Periods for Second Substitution"
                type="number"
                value={minFreePeriodsForSecondSub}
                onChange={(e) =>
                  setMinFreePeriodsForSecondSub(Number(e.target.value))
                }
                fullWidth
                required
                inputProps={{ min: 0 }}
                helperText="Minimum free periods required before a teacher can receive a second substitution in the same day (default: 5)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Total Periods Per Day"
                type="number"
                value={totalPeriodsPerDay}
                onChange={(e) => setTotalPeriodsPerDay(Number(e.target.value))}
                fullWidth
                required
                inputProps={{ min: 1 }}
                helperText="Total periods per school day — used as fallback if class schedule does not specify (default: 8)"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Schedule Settings */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Schedule Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Configure lunch break times and other schedule-related settings.
          </Typography>

          <Grid container spacing={3} mt={1}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Lunch Break Start Time"
                type="time"
                value={lunchStart}
                onChange={(e) => setLunchStart(e.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                error={!!validationErrors.lunchStart}
                helperText={
                  validationErrors.lunchStart ||
                  'Lunch break start time in HH:MM format (default: 12:00)'
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Lunch Break End Time"
                type="time"
                value={lunchEnd}
                onChange={(e) => setLunchEnd(e.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                helperText="Lunch break end time in HH:MM format (default: 13:30)"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Assignment Preferences */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Assignment Preferences
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Configure how the system prioritizes substitute teachers.
          </Typography>

          <FormGroup sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={preferSubjectMatch}
                  onChange={(e) => setPreferSubjectMatch(e.target.checked)}
                />
              }
              label="Prefer subject-qualified teachers"
            />
            <FormHelperText sx={{ ml: 4, mt: -1 }}>
              Prioritize teachers qualified to teach the subject (default: on)
            </FormHelperText>

            <FormControlLabel
              control={
                <Checkbox
                  checked={preferClassAssociation}
                  onChange={(e) => setPreferClassAssociation(e.target.checked)}
                />
              }
              label="Prefer teachers with class association"
            />
            <FormHelperText sx={{ ml: 4, mt: -1 }}>
              Prioritize teachers with a direct class association or same class
              group (default: on)
            </FormHelperText>

            <FormControlLabel
              control={
                <Checkbox
                  checked={avoidConsecutive}
                  onChange={(e) => setAvoidConsecutive(e.target.checked)}
                />
              }
              label="Avoid consecutive substitution periods"
            />
            <FormHelperText sx={{ ml: 4, mt: -1 }}>
              Avoid assigning the same teacher to back-to-back periods (default:
              on)
            </FormHelperText>

            <FormControlLabel
              control={
                <Checkbox
                  checked={preferClassTeacherForSupervision}
                  onChange={(e) =>
                    setPreferClassTeacherForSupervision(e.target.checked)
                  }
                />
              }
              label="Prefer class teacher for break/lunch supervision"
            />
            <FormHelperText sx={{ ml: 4, mt: -1 }}>
              Prioritize the class's own teacher for break and lunch supervision
              periods (default: on)
            </FormHelperText>
          </FormGroup>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Eligible Teacher Types</InputLabel>
                <Select
                  multiple
                  value={eligibleTeacherTypeIds}
                  onChange={(e) =>
                    setEligibleTeacherTypeIds(
                      typeof e.target.value === 'string'
                        ? e.target.value.split(',')
                        : e.target.value
                    )
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => {
                        const type = teacherTypes?.find((t) => t.id === id);
                        return (
                          <Chip
                            key={id}
                            label={type?.type_name || id}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {teacherTypes?.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.type_name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Leave empty to allow all teacher types (default: all eligible)
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Assignment Mode</InputLabel>
                <Select
                  value={assignmentMode}
                  onChange={(e) =>
                    setAssignmentMode(e.target.value as 'auto' | 'suggest')
                  }
                >
                  <MenuItem value="auto">Auto</MenuItem>
                  <MenuItem value="suggest">Suggest</MenuItem>
                </Select>
                <FormHelperText>
                  Auto: system picks substitutes automatically. Suggest: system
                  shows ranked suggestions for admin confirmation (default: Auto)
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Notification Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Configure how substitute teachers are notified.
          </Typography>

          <Grid container spacing={3} mt={1}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Notification Channel</InputLabel>
                <Select
                  value={notificationChannel}
                  onChange={(e) =>
                    setNotificationChannel(
                      e.target.value as 'email' | 'whatsapp' | 'both'
                    )
                  }
                >
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="whatsapp">WhatsApp</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                </Select>
                <FormHelperText>
                  How substitute teachers receive notifications (default: Email)
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Notification Sender Name"
                value={notificationSenderName}
                onChange={(e) => setNotificationSenderName(e.target.value)}
                fullWidth
                helperText="Name shown in notification messages (defaults to school name)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Alert severity="info" sx={{ mt: 1 }}>
                Notification emails are sent from the system email address configured for the app (same as OTP and signup emails).
              </Alert>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Auto-Generation Settings */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Auto-Generation Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Configure automatic draft generation for tomorrow's absences.
          </Typography>

          <Grid container spacing={3} mt={1}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoGenerateEnabled}
                    onChange={(e) => setAutoGenerateEnabled(e.target.checked)}
                  />
                }
                label="Enable automatic draft generation"
              />
              <FormHelperText sx={{ ml: 4, mt: -1 }}>
                When enabled, the system automatically generates tomorrow's draft
                substitution plans each day (default: on)
              </FormHelperText>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Auto-Generate Time"
                type="time"
                value={autoGenerateTime}
                onChange={(e) => setAutoGenerateTime(e.target.value)}
                fullWidth
                required={autoGenerateEnabled}
                disabled={!autoGenerateEnabled}
                InputLabelProps={{ shrink: true }}
                error={!!validationErrors.autoGenerateTime}
                helperText={
                  validationErrors.autoGenerateTime ||
                  'Time of day the automatic draft generation runs (default: 17:00)'
                }
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    color: 'text.disabled',
                  },
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Concurrency Settings */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Concurrency Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Configure how long an admin can hold a lock on a draft plan.
          </Typography>

          <Grid container spacing={3} mt={1}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Lock Timeout (Minutes)"
                type="number"
                value={lockTimeoutMinutes}
                onChange={(e) => setLockTimeoutMinutes(Number(e.target.value))}
                fullWidth
                required
                inputProps={{ min: 1 }}
                helperText="Minutes before a soft lock on a draft plan expires (default: 15)"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? 'Saving...' : 'Save Substitution Rules'}
        </Button>

        {saveMutation.isSuccess && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Substitution rules saved successfully. Changes will apply to all
            future engine runs.
          </Alert>
        )}

        {saveMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Failed to save substitution rules. Please check the form for errors
            and try again.
          </Alert>
        )}
      </Box>
    </Stack>
  );
};
